import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import { Button, Icon, IconButton, InputAdornment, LinearProgress, Paper } from '@mui/material';
import {
  InfoContainer,
  ButtonContainer,
  DropIcon,
  DeleteButton,
  INFO_FORM_STYLE,
  INFO_SCREEN_CONTAINER,
  INFO_SCREEN_CONTAINER_MOBILE,
  INFO_ACCESS_CONTAINER,
  INFO_DROP_ZONE,
  INFO_SCREEN_WIDTH,
} from './ReportAssessmentInfo.styles';
import Alert from '@mui/material/Alert';
import { SETTINGS } from 'feature/admin/account-settings';
import CommentContainer from 'global/components/CommentContainer';
import ConfirmModal from 'global/components/confirm';
import DropRealms from 'global/components/droprealms';
import Form from 'global/components/form';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { Text } from 'global/components/text';
import { useLoaded } from 'global/hooks/useLoaded';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import { first, get, set } from 'global/lib/lodash';
import { useScreen } from 'global/styles';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { UploadFiles } from 'redux/actions/imageUploader';
import sendData from 'redux/actions/sendData';
import { getAccountSetting, userHasPermissions } from 'redux/actions/userLogin';
import { ensureArray } from '../../../global/utils';
import { getOnlyTiersFromScorecards } from '../ReportFab';

const DATEFORMAT = 'l';
const MODDATEFORMAT = 'l h:mma';

export default withRouter((props) => {
  const { assessment, userCanEdit, printView, isTemplate } = props;
  const { data, loading } = assessment;
  const { me } = useUser();
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [confirmModal, setConfirmModal] = useState();
  const [realmId, setRealmId] = useState(null);
  const [realm, setRealm] = useState(get(data, 'realm'));
  const formRef = useRef();
  const [showCreateComment, setShowCreateComment] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const assessmentId = get(assessment, 'data.id');
  const [commentState, setCommentState] = useState({
    type: 'comments',
    attributes: {
      user_id: get(me, 'id'),
      commentable_id: assessmentId,
      commentable_type: 'assessment',
      body: '',
      images: [],
      meta: {},
    },
  });

  const isAuthor = get(data, 'authors', []).find((a) => get(a, 'id') === get(me, 'id'));
  const isEditor = get(data, 'editors', []).find((e) => get(e, 'id') === get(me, 'id'));
  const canSeeComments = isAuthor || isEditor;
  const { isMobileXS } = useScreen();

  const _realm = get(data, 'realm');
  const realmTier = get(realm, 'tier');

  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId } },
      useCache: { reload: 1 },
    }
  );

  const createdBy = get(data, 'authors.0');

  const defaultAccess = getAccountSetting(SETTINGS.assessmentAccess);

  const comments = useRetrieveData(
    { url: `/comments` },
    {
      build: ['comments'],
      waitFor: assessmentId,
      params: {
        filter: {
          commentable_id: assessmentId,
          commentable_type: 'assessment',
        },
        include: 'images,author',
      },
    },
    [assessmentId]
  );

  useEffect(() => {
    setRealm(_realm);
  }, [_realm]);

  useEffect(() => {
    if (realm) {
      setRealmId(realm.id);
    }
  }, [realm]);

  const toggleEdit = async () => {
    if (canEdit) {
      formRef.current.clearState();
    }
    setCanEdit(!canEdit);
  };

  const changeRealm = (obj = {}) => {
    const r = obj[Object.keys(obj)[0]];
    if (r && r.id !== realm.id) {
      setConfirmModal({
        title: 'Confirm',
        content: 'Photos uploaded on the photos page will be deleted if you update the location of your assessment. Do you want to continue?',
        onOk: () => {
          setRealm(r);
          setConfirmModal();
        },
        onCancel: () => {
          setConfirmModal();
        },
      });
    }
  };

  const formatData = async (values) => {
    const relationships = {};
    const scorecards = [];
    const editors = get(values, 'editors', get(data, 'editors', []));
    const viewers = get(values, 'viewers', get(data, 'viewers', []));
    const assignees = ensureArray(get(values, 'assignees', get(data, 'assignees', [])));

    const vis = get(values, 'generalVisibility', 'normal');

    for (let k in values) {
      if (k.startsWith('scorecard')) {
        scorecards.push(values[k]);
      }
    }

    const startDateFormat = 'YYYY-MM-DD';
    const createFormat = 'YYYY-MM-DD HH:mm:ss';

    const attributes = {
      name: get(values, 'name'),
      start_date: moment(get(values, 'startDate', get(data, 'startDate', moment()))).format(startDateFormat),
      end_date: moment(get(values, 'endDate', get(data, 'endDate', moment()))).format(startDateFormat),
      completion_date: get(values, 'completionDate') ? moment(get(values, 'completionDate')).format(startDateFormat) : null,
      visibility: vis === 'normal' ? 1 : vis === 'global' ? 2 : 0,
      status: isTemplate ? get(assessment, 'data.status') : get(values, 'status'),
      description: get(values, 'description'),
      'updated-at': moment().format(createFormat),
      realm_id: get(realm, 'id', '').toString(),
      media: [...get(assessment, 'data.media', []), ...get(values, 'files', [])],
      meta: {
        tasks_enabled: get(values, 'assessmentMeta.tasks_enabled', true),
        targets_enabled: get(values, 'assessmentMeta.targets_enabled', true),
      },
    };

    if (editors) {
      set(relationships, 'editors', {
        data: editors.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    if (viewers) {
      set(relationships, 'viewers', {
        data: viewers.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    if (assignees) {
      set(relationships, 'assignees', {
        data: assignees.map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    const ret = {
      data: {
        type: isTemplate ? 'assessment-templates' : 'assessments',
        id: get(data, 'id', '').toString(),
        attributes,
        relationships,
      },
    };

    await UploadFiles(ret, { attributeType: 'media', isImage: false, collection_name: 'debrief-files' });

    return ret;
  };

  const formSubmit = async (values) => {
    try {
      setFormLoading(true);
      const d = await formatData(values);
      await sendData({
        url: isTemplate ? `/assessment-templates/${data.id}` : `/assessments/${data.id}`,
        method: 'patch',
        data: d,
      });
      props.assessment.update();
      setFormLoading(false);
      setCanEdit(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const dateInputs = canEdit
    ? [
        [
          {
            id: 'startDate',
            type: 'KeyboardDatePicker',
            defaultValue: () => moment(get(data, 'startDate')),
            props: {
              label: 'Visit Start',
            },
          },
          {
            id: 'endDate',
            type: 'KeyboardDatePicker',
            defaultValue: () => moment(get(data, 'endDate')),
            props: {
              label: 'Visit End',
            },
          },
        ],
        {
          id: 'completionDate',
          type: 'KeyboardDatePicker',
          defaultValue: () => (get(data, 'completionDate') ? moment(get(data, 'completionDate')) : ''),
          props: {
            label: 'Delivery Date',
          },
        },
      ]
    : [
        {
          id: 'datess',
          type: 'TextField',
          defaultValue: () => {
            const sDate = get(data, 'startDate');
            const eDate = get(data, 'endDate');
            const dstring = sDate == eDate ? `${moment(sDate).format(DATEFORMAT)}` : `${moment(sDate).format(DATEFORMAT)} - ${moment(eDate).format(DATEFORMAT)}`;
            const cDate = get(data, 'completionDate');
            return dstring + (cDate ? `\nDelivery: ${moment(get(data, 'completionDate')).format(DATEFORMAT)}` : '');
          },
          props: {
            label: 'Dates',
            disabled: !canEdit,
            multiline: true,
          },
        },
      ];

  const onDelete = () => {
    setConfirmModal({
      title: 'Delete Assessment',
      content: 'Are you sure you want to delete this assessment?',
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          setConfirmModal();
          await sendData({
            url: isTemplate ? `/assessment-templates/${data.id}` : `/assessments/${data.id}`,
            method: 'patch',
            data: {
              data: {
                type: isTemplate ? 'assessment-templates' : 'assessments',
                id: data.id,
                attributes: {
                  archived: true,
                },
              },
            },
          });
          props.history.push('/reports');
        } catch (err) {
          setError(`Error: ${err.message}`);
          setFormLoading(false);
        }
      },
    });
  };

  const onDeleteMedia = (file) => {
    setConfirmModal({
      title: 'Delete Media',
      content: `Are you sure you want to delete ${file.name}?`,
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          setConfirmModal();
          await sendData({
            url: `/media/${file.id}`,
            method: 'delete',
          });
          await assessment.update();
          setFormLoading(false);
        } catch (err) {
          setError(`Error: ${err.message}`);
          setFormLoading(false);
        }
      },
    });
  };

  const onUnlock = () => {
    setConfirmModal({
      title: 'Unlock Assessment',
      content: `Unlocking an assessment can create data issues because the person who locked it can make edits offline that conflict with online edits. We suggest you only unlock assessments when you're sure that this person will not make further edits.`,
      okayText: 'Unlock',
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        try {
          setFormLoading(true);
          await sendData({
            url: `/assessments/${data.id}`,
            method: 'patch',
            data: {
              data: {
                id: data.id,
                type: 'assessments',
                attributes: {
                  locked: 0,
                },
              },
            },
          });
          await assessment.update();
        } catch (err) {
        } finally {
          setFormLoading(false);
          setConfirmModal();
        }
      },
    });
  };

  const allUsers = [me, ...users.data];

  useLoaded(props, [loading, users.loading]);

  const handleSubmitComment = async () => {
    try {
      setCommentLoading(true);
      const submitData = {
        data: {
          ...commentState,
          attributes: {
            ...commentState.attributes,
            commentable_id: get(assessment, 'data.id'),
            commentable_type: 'assessment',
          },
        },
      };

      await UploadFiles(submitData, { attributeType: 'images' });
      await sendData({
        url: '/comments',
        method: 'post',
        data: submitData,
      });
      comments.update();
      setShowCreateComment(false);
      set(commentState, 'attributes.body', '');
      set(commentState, 'attributes.images', []);
    } catch (err) {
    } finally {
      setCommentLoading(false);
    }
  };

  return !data || loading || users.loading ? (
    <LinearProgress />
  ) : (
    <InfoContainer isMobile={isMobileXS} printView={printView}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {!printView && (
        <ButtonContainer>
          {!formLoading && userCanEdit && (
            <Button color={canEdit ? 'notice' : 'secondary'} variant="contained" onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
              {'Edit'}
            </Button>
          )}
          {!formLoading && canEdit && userCanEdit && (
            <DeleteButton color="error" variant="contained" onClick={onDelete}>
              <Icon>delete</Icon>
              {'Delete'}
            </DeleteButton>
          )}
          {!formLoading && !!data.locked && userHasPermissions(['manageAccount']) && (
            <Button color={'secondary'} style={{ marginLeft: 8 }} variant="contained" onClick={onUnlock} startIcon={<Icon>{'lock'}</Icon>}>
              {'Unlock'}
            </Button>
          )}
        </ButtonContainer>
      )}

      <Form
        loading={formLoading}
        showAllScreens={true}
        formStyle={INFO_FORM_STYLE}
        onSubmit={formSubmit}
        canSubmit={canEdit}
        ref={formRef}
        submitText="Save Changes"
        screens={[
          [
            {
              title: 'General Info',
              screenContainerStyle: isMobileXS ? INFO_SCREEN_CONTAINER_MOBILE : INFO_SCREEN_CONTAINER,
              screenContainer: {
                props: {
                  className: INFO_SCREEN_WIDTH,
                },
              },
              inputs: [
                {
                  props: {
                    label: 'Location',
                    disabled: !canEdit,
                  },
                  component: (
                    <DropRealms
                      title={get(realm, 'name')}
                      openToSelected={true}
                      modalTitle={'SET LOCATION'}
                      onlyTiers={isTemplate ? ['builder', 'division', 'community'] : getOnlyTiersFromScorecards(data.scorecards)}
                      hideShow={true}
                      showModify={canEdit}
                      singleSelect
                      currentRealm={realm}
                      onUpdate={changeRealm}
                      realms={[realm]}
                    />
                  ),
                },
                {
                  id: 'name',
                  type: 'TextField',
                  defaultValue: () => get(data, 'name', ''),
                  props: {
                    label: 'Title',
                    disabled: !canEdit,
                  },
                },
                ...dateInputs,
                !isTemplate && {
                  id: 'status',
                  type: 'Select',
                  defaultValue: () => get(data, 'status'),
                  data: [
                    { title: 'Draft', value: 'draft' },
                    { title: 'Review', value: 'review' },
                    { title: 'Final', value: 'published' },
                  ],
                  props: {
                    label: 'Status',
                    disabled: !canEdit,
                  },
                },
                {
                  id: 'description',
                  type: 'RichText',
                  defaultValue: () => get(data, 'description', ''),
                  props: {
                    label: 'Description',
                    placeholder: 'Description',
                    disabled: !canEdit,
                  },
                },
                !printView && {
                  id: 'files',
                  type: 'DropZone',
                  props: {
                    disabled: !canEdit,
                    label: 'Files',
                    dropzoneText: (
                      <>
                        Drag and Drop or Click to Browse
                        <DropIcon>folder_open</DropIcon>
                      </>
                    ),
                    onDeleteFile: (file) => {
                      onDeleteMedia(file);
                    },
                    initialFiles: get(assessment, 'data.media', []),
                    fullWidth: true,
                    filesLimit: 100,
                    useChipsForPreview: true,
                    maxFileSize: 315922080,
                    dropzoneClass: INFO_DROP_ZONE,
                  },
                },
              ].filter((f) => f),
            },
            {
              title: 'More Details',
              collapsable: { collapsed: !printView },
              screenContainerStyle: isMobileXS ? INFO_SCREEN_CONTAINER_MOBILE : INFO_SCREEN_CONTAINER,
              screenContainer: {
                props: {
                  className: INFO_SCREEN_WIDTH,
                },
              },
              inputs: [
                {
                  id: 'locationname',
                  type: 'TextField',
                  defaultValue: () => `${get(data, 'realm.parent.name')} - ${get(realm, 'name')}`,
                  props: {
                    label: 'Location',
                    disabled: true,
                    InputProps: {
                      startAdornment: <InputAdornment position="start">{realmTier === 'division' ? <DivisionIcon /> : realmTier === 'community' ? <CommunityIcon /> : <HomeIcon />}</InputAdornment>,
                    },
                  },
                },
                {
                  id: 'criteria',
                  type: 'TextField',
                  defaultValue: () =>
                    get(data, 'scorecards', [])
                      .map((sc) => get(sc, 'name', 'Unknown'))
                      .join('\n'),
                  props: {
                    label: 'Criteria',
                    disabled: true,
                    multiline: true,
                  },
                },
                {
                  id: 'created',
                  type: 'TextField',
                  defaultValue: () => `${moment(get(data, 'createdAt')).format(MODDATEFORMAT)} - ${get(createdBy, 'name', 'UNKNOWN')}`,
                  props: {
                    label: 'Created',
                    disabled: true,
                  },
                },
                {
                  id: 'modified',
                  type: 'TextField',
                  defaultValue: () => `${moment(get(data, 'updatedAt')).format(MODDATEFORMAT)}`,
                  props: {
                    label: 'Modified',
                    disabled: true,
                  },
                },
              ],
            },
            canSeeComments &&
              !printView && {
                title: 'Comments',
                collapsable: { collapsed: true },
                screenContainerStyle: isMobileXS ? INFO_SCREEN_CONTAINER_MOBILE : INFO_SCREEN_CONTAINER,
                screenContainer: {
                  props: {
                    className: INFO_SCREEN_WIDTH,
                  },
                },
                inputs: [
                  {
                    id: 'comments',
                    component: () => (
                      <CommentContainer
                        data={data}
                        commentableId={assessmentId}
                        commentableType={'assessment'}
                        commentState={commentState}
                        setCommentState={setCommentState}
                        commentLoading={commentLoading}
                        showCreateComment={showCreateComment}
                        showTitle={false}
                        onShowCreateComment={setShowCreateComment}
                        onSubmitComment={handleSubmitComment}
                        comments={comments}
                        helperText={
                          <Text variant="display-small">
                            Assessment comments are only visible to <strong>assessment editors</strong>. The intended use is for suggesting modifications or having other administrative dialogue.
                          </Text>
                        }
                      />
                    ),
                  },
                ],
              },
          ].filter((f) => f),
          !printView && {
            title: 'Access',
            screenContainerStyle: isMobileXS ? INFO_SCREEN_CONTAINER_MOBILE : INFO_SCREEN_CONTAINER,
            screenContainer: {
              component: Paper,
              props: {
                elevation: 5,
                square: true,
                className: INFO_ACCESS_CONTAINER,
              },
            },
            inputs: [
              {
                id: 'assignees',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => first(get(data, 'assignees', [])),
                props: {
                  label: 'Assignee',
                  helperText: isTemplate ? 'This user will be the default Assignee for all assessments created from this template' : 'These users can edit this assessment',
                  disabled: !canEdit,
                  size: 'small',
                  required: get(realm, 'tier') === 'lot',
                  multiple: false,
                  helperText: (fs) => {
                    const keys = {
                      draft: 'This user won’t see scores or highlights until the status is set to “Review”',
                      review: 'This user can see scores and highlights since the status is “Review”',
                      published: 'This user can view this assessment',
                      template: 'This user will be the default Assignee for all assessments created from this template',
                    };

                    if (isTemplate) {
                      return keys.template;
                    }

                    return get(keys, get(assessment, 'data.status'), keys.draft);
                  },
                },
              },
              {
                id: 'editors',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => get(data, 'editors', []),
                props: {
                  label: 'Editors',
                  helperText: isTemplate
                    ? 'These users can use and edit this template and will be assigned as Editors on all assessments created from this template'
                    : 'These users can edit this assessment',
                  disabled: !canEdit,
                  size: 'small',
                },
              },
              {
                id: 'viewers',
                type: 'UserSelect',
                data: () => allUsers,
                defaultValue: () => get(data, 'viewers', []),
                props: {
                  label: 'Viewers',
                  size: 'small',
                  helperText: (formState) => {
                    if (isTemplate) {
                      return 'These users will be assigned as Viewers on all assessments created from this template';
                    }
                    const statusText =
                      get(formState, 'status') === 'draft'
                        ? ` but won't see scores or highlights until the status is set to "Review"`
                        : get(formState, 'status') === 'review'
                          ? ` and can see scores and highlights since the status is "Review"`
                          : '';
                    return `These users can view this assessment${statusText}`;
                  },
                  disabled: !canEdit,
                },
              },
              {
                id: 'generalVisibility',
                type: 'ToggleButton',
                options: [
                  { title: 'NORMAL', value: 'normal' },
                  { title: 'GLOBAL', value: 'global' },
                  { title: 'HIDDEN', value: 'hidden' },
                ],
                defaultValue: () => get(data, 'visibility', 'normal'),
                props: {
                  exclusive: true,
                  label: 'General Visibility',
                  disabled: !canEdit,
                  helperText: (formState) => {
                    const gVis = get(formState, 'generalVisibility');
                    if (isTemplate) {
                      const keys = {
                        normal: `(default) Assessments created from this template will be assigned normal access: all users with access to that new assessment's location will see it.`,
                        global: `Assessments created from this template will be assigned global access: all users in the company will see it.`,
                        hidden: `Assessments created from this template will be assigned hidden access: no users will see it except the Author and those assigned as the Assignee, Editors, or Viewers.`,
                      };

                      return get(keys, gVis);
                    }

                    const statusText = get(formState, 'status') !== 'published' ? ` However, they won't see scores or highlights until the status is set to "Final".` : '';

                    let str;

                    const getText = (key, text) => {
                      let t = '';
                      if (key === defaultAccess) {
                        t += '(default) ';
                      }
                      t += text;
                      return t;
                    };

                    if (gVis === 'normal') {
                      str = `All users with access to ${get(realm, 'name', '[Realm Name]')} can view this assessment.${statusText}`;
                    } else if (gVis === 'global') {
                      str = `All users in the company can view this assessment.${statusText}`;
                    } else if (gVis === 'hidden') {
                      str = 'No users can see this assessment except those assigned above.';
                    } else {
                      str = '';
                    }

                    return getText(gVis, str);
                  },
                },
              },
              {
                id: 'assessmentMeta',
                type: 'Checkbox',
                defaultValue: () => {
                  const meta = get(data, 'meta', {});
                  return {
                    tasks_enabled: meta.tasksEnabled !== false,
                    targets_enabled: meta.targetsEnabled !== false,
                  };
                },
                options: [
                  { label: 'Create Tasks', value: 'tasks_enabled' },
                  { label: 'Update New Targets', value: 'targets_enabled' },
                ],
                props: {
                  label: 'Assessment Features',
                  disabled: !canEdit,
                  showAll: true,
                },
              },
            ],
          },
        ].filter((f) => f)}
      />
      <ConfirmModal {...confirmModal} />
    </InfoContainer>
  );
});
